import type { UIEvent } from "react";

import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from "@headlessui/react";
import { MagnifyingGlassIcon, XCircleIcon } from "@phosphor-icons/react";

import { classNames } from "@alpacahq/ui/old";

import type { Vasp } from "~/v2/api/rest/cryptoWallet";
import { InputField } from "~/v2/components/common/InputField";
import type { VaspSearchState } from "../useVaspSearch";
import {
  DID_ENTRY,
  MANUAL_ENTRY,
  type Selection,
  type UseVaspSearchDropdownControllerReturn,
} from "./VaspSearchDropdown.controller";

export interface VaspSearchDropdownPresentationProps
  extends UseVaspSearchDropdownControllerReturn {
  vaspSearch: VaspSearchState;
  vaspSelectionError?: string;
  manualVaspNameError?: string;
  manualVaspWebsiteError?: string;
}

// Distance from the bottom of the list at which the next page is requested.
const LOAD_MORE_THRESHOLD_PX = 50;

const onResultsScroll = (
  event: UIEvent<HTMLElement>,
  { hasMore, isLoadingMore, onLoadMore }: VaspSearchState,
) => {
  const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
  if (
    scrollHeight - scrollTop - clientHeight < LOAD_MORE_THRESHOLD_PX &&
    hasMore &&
    !isLoadingMore
  ) {
    onLoadMore();
  }
};

export const VaspSearchDropdownPresentation = ({
  vaspSearch,
  inputRef,
  selection,
  isLocked,
  showPanel,
  showClearButton,
  didCandidate,
  onChange,
  onQueryChange,
  displayValue,
  onClear,
  vaspSelectionError,
  manualVaspNameError,
  manualVaspWebsiteError,
}: VaspSearchDropdownPresentationProps) => (
  <div className="flex flex-col gap-2">
    <Combobox<Selection>
      value={selection}
      onChange={onChange}
      disabled={isLocked}
    >
      <div className="relative">
        <label className="mb-1 block font-semibold text-gray-700 text-sm">
          Custodian or Exchange
        </label>
        <div className="relative">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <MagnifyingGlassIcon size={16} className="text-gray-400" />
          </div>
          <ComboboxInput
            ref={inputRef}
            name="vasp-search"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            data-1p-ignore
            data-lpignore="true"
            readOnly={isLocked}
            displayValue={displayValue}
            onChange={(e) => onQueryChange(e.target.value)}
            placeholder="Enter name, select custodian / exchange or enter VASP id"
            className={classNames(
              "w-full rounded border border-gray-400 bg-white py-2 pr-9 pl-9 text-sm",
              "focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500",
            )}
          />
          {showClearButton && (
            <button
              type="button"
              onClick={onClear}
              className="absolute inset-y-0 right-0 flex items-center pr-3"
              aria-label="Clear search"
            >
              <XCircleIcon size={16} className="text-gray-400" />
            </button>
          )}
        </div>

        {showPanel && (
          <ComboboxOptions
            onScroll={(e) => onResultsScroll(e, vaspSearch)}
            className="absolute z-10 mt-1 max-h-64 w-full overflow-auto rounded border border-gray-300 bg-white shadow-lg"
          >
            {/* Rendered outside the loading/results branches so a pasted DID is
                actionable immediately rather than after the debounced search
                resolves to "No matches found". */}
            {didCandidate && (
              <ComboboxOption
                value={DID_ENTRY}
                className={classNames(
                  "flex w-full cursor-pointer flex-col gap-0.5 border-gray-100 border-b px-3 py-2 text-left text-sm",
                  "hover:bg-indigo-50 active:bg-indigo-50",
                )}
              >
                <span className="font-medium text-gray-900">Use VASP ID</span>
                <span className="break-all font-mono text-gray-500 text-xs">
                  {didCandidate}
                </span>
              </ComboboxOption>
            )}

            {vaspSearch.isLoading ? (
              <div
                role="status"
                aria-live="polite"
                aria-label="Searching custodians"
              >
                {[0, 1].map((i) => (
                  <div
                    key={i}
                    className="flex flex-col gap-1.5 border-gray-100 border-b px-3 py-2 last:border-b-0"
                  >
                    <div className="h-3.5 w-2/5 animate-pulse rounded bg-gray-200" />
                    <div className="h-3 w-3/5 animate-pulse rounded bg-gray-200" />
                  </div>
                ))}
              </div>
            ) : vaspSearch.results.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-3 p-8">
                <p className="m-0 p-0 text-center font-semibold text-gray-800 text-lg">
                  No matches found
                </p>
                <p className="m-0 p-0 text-center text-gray-800 text-sm">
                  We couldn't find a match for{" "}
                  <span className="font-semibold">"{vaspSearch.query}"</span>.
                  You can try a different search or add it manually by providing
                  a name and URL.
                </p>
                <ComboboxOption
                  value={MANUAL_ENTRY}
                  className={classNames(
                    "flex cursor-pointer items-center justify-center rounded px-4 py-2",
                    "text-center font-medium text-gray-950 text-sm leading-5",
                    "border-none transition-colors",
                    "bg-yellow-400 hover:bg-yellow-400 active:bg-yellow-300",
                  )}
                >
                  Add Manually
                </ComboboxOption>
              </div>
            ) : (
              <>
                {vaspSearch.results.map((vasp: Vasp, index) => (
                  <ComboboxOption
                    key={vasp.id || `vasp-option-${index}`}
                    value={vasp}
                    className={classNames(
                      "flex w-full cursor-pointer flex-col gap-0.5 px-3 py-2 text-left text-sm",
                      "hover:bg-indigo-50 active:bg-indigo-50",
                    )}
                  >
                    <span className="font-medium text-gray-900">
                      {vasp.name}
                    </span>
                    <div className="flex items-center gap-2 text-gray-500 text-xs">
                      {vasp.website && <span>{vasp.website}</span>}
                      {vasp.country && (
                        <span className="inline-flex items-center gap-1">
                          <span aria-hidden>•</span>
                          <span>{vasp.country}</span>
                        </span>
                      )}
                    </div>
                  </ComboboxOption>
                ))}
                {vaspSearch.isLoadingMore && (
                  <div
                    role="status"
                    aria-live="polite"
                    aria-label="Loading more custodians"
                    className="flex flex-col gap-1.5 border-gray-100 border-t px-3 py-2"
                  >
                    <div className="h-3.5 w-2/5 animate-pulse rounded bg-gray-200" />
                    <div className="h-3 w-3/5 animate-pulse rounded bg-gray-200" />
                  </div>
                )}

                <ComboboxOption
                  value={MANUAL_ENTRY}
                  className={
                    "flex w-full cursor-pointer flex-col gap-0.5 px-3 py-2 text-left font-medium text-gray-900 text-sm hover:bg-indigo-50 active:bg-indigo-50"
                  }
                >
                  Add Manually
                </ComboboxOption>
              </>
            )}
          </ComboboxOptions>
        )}
      </div>
    </Combobox>

    {vaspSelectionError && (
      <p className="m-0 mt-1 p-0 text-red-500 text-xs">{vaspSelectionError}</p>
    )}

    {/* Selected-VASP detail panel — surfaces the DID and jurisdiction
        stored on the chosen VASP so users can confirm they picked the
        right entity before submitting. The cryptowallet backend echoes
        the VASP DID into the wire payload as `beneficiary_vasp_id`,
        and the user has no other place to see what was selected. */}
    {vaspSearch.selectedVasp && (
      <div className="mt-2 flex flex-col gap-1 rounded border border-gray-200 bg-gray-50 p-2 text-gray-600 text-xs">
        {vaspSearch.selectedVasp.did && (
          <div className="flex items-baseline gap-2">
            <span className="shrink-0 font-medium text-gray-700">DID</span>
            <span className="break-all font-mono text-gray-800">
              {vaspSearch.selectedVasp.did}
            </span>
          </div>
        )}
        {vaspSearch.selectedVasp.country && (
          <div className="flex items-baseline gap-2">
            <span className="shrink-0 font-medium text-gray-700">
              Jurisdiction
            </span>
            <span className="text-gray-800">
              {vaspSearch.selectedVasp.country}
            </span>
          </div>
        )}
        {vaspSearch.selectedVasp.website &&
          !vaspSearch.selectedVasp.did &&
          !vaspSearch.selectedVasp.country && (
            <div className="flex items-baseline gap-2">
              <span className="shrink-0 font-medium text-gray-700">
                Website
              </span>
              <span className="break-all text-gray-800">
                {vaspSearch.selectedVasp.website}
              </span>
            </div>
          )}
      </div>
    )}

    {/* A hand-entered DID carries no directory name, and cryptowallet rejects
        exchange addresses whose `institution_name` is empty — but only the
        create endpoint takes that field, so skip this when editing. */}
    {vaspSearch.isDidEntry && vaspSearch.didEntryNameRequired && (
      <InputField
        label="Custodian or Exchange Name"
        value={vaspSearch.didEntryName}
        onChange={(e) => vaspSearch.setDidEntryName(e.target.value)}
        onBlur={vaspSearch.onBlurDidEntryName}
        error={vaspSearch.didEntryNameError}
        fullWidth
      />
    )}

    {vaspSearch.isManualEntry && (
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <InputField
          label="Name"
          value={vaspSearch.manualVaspName}
          onChange={(e) => vaspSearch.setManualVaspName(e.target.value)}
          error={manualVaspNameError}
          fullWidth
        />
        <InputField
          label="URL"
          value={vaspSearch.manualVaspWebsite}
          onChange={(e) => vaspSearch.setManualVaspWebsite(e.target.value)}
          placeholder="https://"
          error={manualVaspWebsiteError}
          fullWidth
        />
      </div>
    )}
  </div>
);
